/**
 * Vehicle check and defect report hooks (docs/mobile-design.md Phase 3, chunk 2). Keys share the web's
 * ['fleet', ws, …] prefix. Photos are data URLs (resized by the app) uploaded to the fleet store when sent; the
 * server puts them on the issue the check or report raises. With no signal (or when the signal drops mid-send) the
 * check or report is saved in the run outbox with the same Idempotency-Keys and sent, once, later. No DOM.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, classifyOutcome, type CheckResult, type FleetReportKind, type ReportResult } from '@tradeoutlet/shared';
import { dataApi } from './client';
import { qk } from './keys';
import { invalidateOps } from './ops';
import { runOutbox } from './runs';
import type { RunOutboxOp } from './runOutbox';

type Ws = string | null | undefined;

export function useFleetVehicles(ws: Ws) {
  return useQuery({ queryKey: qk.fleet(ws ?? null, 'vehicles', false), queryFn: () => dataApi().fleet.vehicles(ws!), enabled: Boolean(ws), staleTime: 5 * 60_000, networkMode: 'offlineFirst' });
}

/** The driver's own defect / damage / accident reports (newest first). */
export function useMyFleetReports(ws: Ws) {
  return useQuery({ queryKey: qk.fleet(ws ?? null, 'reports', 'mine'), queryFn: () => dataApi().fleet.reports(ws!), enabled: Boolean(ws), staleTime: 60_000 });
}

/** Idempotency-Keys for one send: the check / report itself and each photo upload (reused if it has to be queued). */
export interface SendKeys { id: string; photos: string[] }

async function uploadAll(ws: string, photos: string[], keys?: string[]): Promise<string[]> {
  const ids: string[] = [];
  for (const [i, p] of photos.entries()) ids.push((await dataApi().fleet.upload(ws, p, keys?.[i])).id);
  return ids;
}

export interface CheckInput { vehicle_id: string; journey_id: string | null; odometer_miles: number | null; items: { key: string; ok: boolean; note: string | null }[]; photos: string[] }
/** Photos first, then the check (a failed check raises an issue with them). */
export async function submitCheckWithPhotos(ws: string, b: CheckInput, keys?: SendKeys) {
  return dataApi().fleet.submitCheck(ws, { vehicle_id: b.vehicle_id, journey_id: b.journey_id, odometer_miles: b.odometer_miles, items: b.items, photo_upload_ids: await uploadAll(ws, b.photos, keys?.photos) }, keys?.id);
}
export function useSubmitCheck(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: CheckInput) => submitCheckWithPhotos(ws!, b),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.fleet(ws ?? null) }); void invalidateOps(qc, ws); },
  });
}

export interface DefectInput { vehicle_id: string; journey_id: string | null; kind: FleetReportKind; severity: 'low' | 'medium' | 'high'; description: string; off_road: boolean; photos: string[]; pin: { lat: number; lng: number } | null }
/** Photos first, then the report (it raises an issue with them). Accidents are always high. */
const reportBody = (b: DefectInput) => ({
  vehicle_id: b.vehicle_id, journey_id: b.journey_id, kind: b.kind, severity: b.kind === 'accident' ? 'high' as const : b.severity, description: b.description.trim(),
  off_road: b.off_road, lat: b.pin?.lat ?? null, lng: b.pin?.lng ?? null,
});
export async function reportDefectWithPhotos(ws: string, b: DefectInput, keys?: SendKeys) {
  return dataApi().fleet.report(ws, { ...reportBody(b), photo_upload_ids: await uploadAll(ws, b.photos, keys?.photos) }, keys?.id);
}
export function useReportDefect(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (b: DefectInput) => reportDefectWithPhotos(ws!, b),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.fleet(ws ?? null) }); void invalidateOps(qc, ws); },
  });
}

// ------------------------------------------------------------------------------------------------ send, or save for later
/** No answer, a timeout, 5xx or 429: worth saving and resending. A refusal (4xx) is shown to the driver instead. */
export const isRetryableSendError = (e: unknown) => classifyOutcome(e instanceof ApiError ? e.status : null) === 'retry';

export type SendOutcome<R> = { queued: false; result: R } | { queued: true; op: RunOutboxOp };

const files = (photos: string[], keys: string[]) => photos.map((data_url, i) => ({ key: keys[i]!, data_url }));

/** Save a check in the outbox (keyed per vehicle, so it never waits behind a run). */
export function queueCheck(ws: string, b: CheckInput, keys: SendKeys, label: string) {
  return runOutbox().enqueue({
    id: keys.id, kind: 'fleet.check', workspaceId: ws, journeyId: `vehicle:${b.vehicle_id}`, label,
    payload: { check: { vehicle_id: b.vehicle_id, journey_id: b.journey_id, odometer_miles: b.odometer_miles, items: b.items }, photos: files(b.photos, keys.photos) },
  });
}
export function queueReport(ws: string, b: DefectInput, keys: SendKeys, label: string) {
  return runOutbox().enqueue({ id: keys.id, kind: 'fleet.report', workspaceId: ws, journeyId: `vehicle:${b.vehicle_id}`, label, payload: { report: reportBody(b), photos: files(b.photos, keys.photos) } });
}

/**
 * Send now when online (so the driver gets the issue number straight away); with no signal, or if the signal drops
 * mid-send, save it in the outbox with the same keys (a send whose answer was lost can't run twice).
 */
async function sendOrQueue<R>(online: boolean, photos: number, direct: (k: SendKeys) => Promise<R>, queue: (k: SendKeys) => Promise<RunOutboxOp>): Promise<SendOutcome<R>> {
  const o = runOutbox();
  const keys: SendKeys = { id: o.newKey(), photos: Array.from({ length: photos }, () => o.newKey()) };
  if (!online) return { queued: true, op: await queue(keys) };
  try { return { queued: false, result: await direct(keys) }; }
  catch (e) { if (isRetryableSendError(e)) return { queued: true, op: await queue(keys) }; throw e; }
}

export const sendCheck = (ws: string, v: { input: CheckInput; online: boolean; label: string }): Promise<SendOutcome<CheckResult>> =>
  sendOrQueue(v.online, v.input.photos.length, k => submitCheckWithPhotos(ws, v.input, k), k => queueCheck(ws, v.input, k, v.label));
export const sendReport = (ws: string, v: { input: DefectInput; online: boolean; label: string }): Promise<SendOutcome<ReportResult>> =>
  sendOrQueue(v.online, v.input.photos.length, k => reportDefectWithPhotos(ws, v.input, k), k => queueReport(ws, v.input, k, v.label));

export function useSendCheck(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    // 'always': with no signal it must run (and save to the outbox), not pause
    networkMode: 'always',
    mutationFn: (v: { input: CheckInput; online: boolean; label: string }) => sendCheck(ws!, v),
    onSuccess: r => { if (!r.queued) { void qc.invalidateQueries({ queryKey: qk.fleet(ws ?? null) }); void invalidateOps(qc, ws); } },
  });
}
export function useSendReport(ws: Ws) {
  const qc = useQueryClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (v: { input: DefectInput; online: boolean; label: string }) => sendReport(ws!, v),
    onSuccess: r => { if (!r.queued) { void qc.invalidateQueries({ queryKey: qk.fleet(ws ?? null) }); void invalidateOps(qc, ws); } },
  });
}

/** Cache wiring for the app's outbox once a saved check or report has gone. */
export function applyFleetSent(qc: QueryClient, op: RunOutboxOp) {
  void qc.invalidateQueries({ queryKey: qk.fleet(op.workspaceId) });
  void invalidateOps(qc, op.workspaceId);
}
